import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Hint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { PayrollRunEmptyStateCalloutDocument } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Loading from 'modules/core/components/Loading'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import organizationsBulkActions from 'modules/compensations/pages/CompensationsPayrollRun/PayrollRunEmptyStateCallout/assets/organizations-bulk-actions.jpg'

type PayrollRunEmptyStateCallOutProps = {
  payrollRun: {
    id: number
  }
}

const PayrollRunEmptyStateCallout = ({ payrollRun }: PayrollRunEmptyStateCallOutProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const dismissKey = `${LOCAL_STORAGE_KEYS.showCompensationsEmptyStateCallout}_${payrollRun.id}`

  const [showHint, setShowHint] = useState(false)
  const [isDismissed, setIsDismissed] = useLocalStorage<boolean>(dismissKey, false)

  // TODO: Lift this query to the page scope (CompensationsPayrollRun.graphql)
  // to avoid the second backend call. Fetch together with the page query and pass only the needed
  // fields as props to <PayrollRunEmptyStateCallout/>. When doing this:
  // 1) Move PayrollRunEmptyStateCalloutDocument into CompensationsPayrollRun.graphql,
  // 2) Remove this useQuery + local loading/error handling,
  // Remove after the feature is validated and the page-level data flow is in place. (ref: PR comment by David Abba)
  const { data, loading, error } = useQuery(PayrollRunEmptyStateCalloutDocument, {
    variables: {
      payrollRunId: payrollRun.id,
    },
  })

  const hasEmployeesWithoutDefinedCompensations = useMemo(() => {
    const contractVersions = data?.contracts?.contractVersionsConnection?.nodes || []

    return contractVersions.some(
      (contractVersion) => contractVersion?.additionalCompensationsConnection?.totalCount === 0
    )
  }, [data?.contracts?.contractVersionsConnection?.nodes])

  const hasAtLeastOneAdditionalCompensation = useMemo(() => {
    const contractVersions = data?.contracts?.contractVersionsConnection?.nodes || []

    return contractVersions.some(
      (contractVersion) => (contractVersion?.additionalCompensationsConnection?.totalCount ?? 0) > 0
    )
  }, [data?.contracts?.contractVersionsConnection?.nodes])

  // Handle dismissing the callout
  const handleDismiss = () => {
    setShowHint(false)

    // If at least 1 additional compensation is defined in contracts,
    // don't show callout again (Scenario 2)
    if (hasAtLeastOneAdditionalCompensation) {
      setIsDismissed(true)
    } else {
      // If no additional compensations, clear dismissed state so callout shows again (Scenario 1)
      setIsDismissed(false)
    }
  }

  const shouldShowCallout = useMemo(() => {
    if (loading || !data) {
      return false
    }

    if (!hasEmployeesWithoutDefinedCompensations) {
      return false
    }

    // If dismissed and at least 1 additional compensation exists, don't show
    if (isDismissed === true && hasAtLeastOneAdditionalCompensation) {
      return false
    }

    // Otherwise, show the callout
    return true
  }, [
    loading,
    data,
    hasEmployeesWithoutDefinedCompensations,
    isDismissed,
    hasAtLeastOneAdditionalCompensation,
  ])

  if (error) {
    throw new Error('Error fetching data')
  }

  if (loading) {
    return <Loading />
  }

  // If we shouldn't show callout based on rules, return null
  if (!shouldShowCallout) {
    return null
  }

  // Count employees without additional compensations for displaying in the message
  const employeesWithoutCompensationsCount = (
    data?.contracts?.contractVersionsConnection?.nodes || []
  ).filter((node) => node?.additionalCompensationsConnection?.totalCount === 0).length

  return (
    <>
      <Callout
        type="info"
        compact
        description={i18n.t('compensation.empty_state.message', {
          count: employeesWithoutCompensationsCount,
        })}
        action={
          <Button type="secondary" onClick={() => setShowHint(true)}>
            {i18n.t('compensation.empty_state.learn_more')}
          </Button>
        }
      />

      {showHint && (
        <Hint.NewModal onClose={handleDismiss} onFinish={handleDismiss}>
          <Hint.NewModal.ImageStep
            src={organizationsBulkActions}
            title={i18n.t('compensation.empty_state.modal_title')}
            description={i18n.t('compensation.empty_state.modal_description')}
          >
            <Box
              textAlign="center"
              alignItems="center"
              justifyContent="center"
              flexDirection="row"
              gap="s16"
            >
              <F0Button
                label={i18n.t('compensation.empty_state.dismiss')}
                onClick={handleDismiss}
                size="md"
                variant="outline"
                weight="medium"
                type="button"
              />
              <F0Button
                label={i18n.t('compensation.empty_state.go_to_organizations')}
                onClick={() => navigate(navigation.employees.list.fullPath())}
                size="md"
                variant="default"
                weight="medium"
                type="button"
              />
            </Box>
          </Hint.NewModal.ImageStep>
        </Hint.NewModal>
      )}
    </>
  )
}

export { PayrollRunEmptyStateCallout }
